/**
 * Footprint check — does every part on the sheet have a board part to become?
 *
 * Run when a schematic is opened, and from the Netlist panel. It lists each
 * package, says whether a pattern was found for it and how, and lets an
 * unresolved one be matched by hand against the libraries.
 *
 * **It cannot break connectivity.** Matching writes one field on the symbol —
 * the pattern's name — and nothing else. No pad, no pin, no signal and no id is
 * touched on either drawing, so this is safe on a design already netlisted and
 * already routed: the next Net List In simply finds the pattern it could not
 * find before, and every net stays where it was.
 */

import type { CadDocument, ComponentEntity } from '@sircad/core/model.ts';
import {
  checkFootprints, setFootprint, type FootprintCheck,
} from '@sircad/core/net/footprints.ts';
import { button, buttons, openDialog } from './dialog.ts';

export interface FootprintPartHit {
  id: string;
  name: string;
  description: string;
  library: string;
}

/** A footprint the catalogue proposes, and the reason it proposes it. */
export interface FootprintSuggestion {
  name: string;
  description: string;
  library: string;
  /** In words the operator can check — "28 pads for 28 pins, the part says DIP". */
  why: string;
}

export interface FootprintDialogOptions {
  /** Every sheet of the design. A package’s gates may be spread across them. */
  sheets: readonly CadDocument[];
  /** Resolve a pattern name to a placeable board part. */
  findFootprint(name: string): Promise<ComponentEntity | null>;
  /** Search the PCB libraries by keyword. */
  searchFootprints(query: string): Promise<FootprintPartHit[]>;
  /**
   * What this package could be, best first.
   *
   * Searching was always possible; knowing what to type was the problem, and
   * it is why a sheet transfers to a board and places nothing. These come from
   * the pin count and the part number, so they are offered before the box.
   */
  suggest(need: { part: string; pins: number }): FootprintSuggestion[];
  /** Called after any match is made, so the shell records undo and redraws. */
  onChange(label: string): void;
}

/** How many results a search shows before it asks for a narrower word. */
const MAX_HITS = 40;

export function showFootprintDialog(opts: FootprintDialogOptions): void {
  openDialog({
    title: 'Footprint Check',
    wide: true,
    build(body, handle) {
      const summary = document.createElement('p');
      summary.className = 'dfm-note';
      summary.textContent = 'Checking…';
      body.append(summary);

      const list = document.createElement('div');
      list.className = 'net-list';
      body.append(list);

      const bar = buttons(body);
      const recheck = button(bar, 'Check again', () => void refresh());
      button(bar, 'Close', () => handle.close());

      /** The package whose search pane is open, so only one is ever showing. */
      let openRef: string | null = null;

      async function refresh(): Promise<void> {
        recheck.disabled = true;
        summary.textContent = 'Checking…';
        let checks: FootprintCheck[];
        try {
          checks = await checkFootprints(opts.sheets, opts.findFootprint);
        } catch (e) {
          summary.textContent = `Could not check: ${(e as Error).message}`;
          recheck.disabled = false;
          return;
        }
        recheck.disabled = false;

        const bad = checks.filter((c) => c.status === 'unresolved');
        const guessed = checks.filter((c) => c.status === 'by-part-name');

        summary.textContent = checks.length === 0
          ? 'This sheet has no annotated parts on it yet. Run Annotate first.'
          : bad.length === 0
            ? `All ${checks.length} package${checks.length === 1 ? '' : 's'} have a pattern`
              + (guessed.length > 0
                ? ` — ${guessed.length} found by part name rather than named by the sheet, `
                  + 'worth a look. '
                : '. ')
              + 'Change any of them with the button on its row.'
            : `${bad.length} of ${checks.length} package${checks.length === 1 ? '' : 's'} `
              + 'have no pattern to become. Pick one for each, or load onto the board '
              + 'without them.';

        list.replaceChildren();
        // The unresolved first: they are the reason the dialog is open.
        const order = [...checks].sort((a, b) => rank(a) - rank(b));
        for (const c of order) render(c);
      }

      function rank(c: FootprintCheck): number {
        return c.status === 'unresolved' ? 0 : c.status === 'by-part-name' ? 1 : 2;
      }

      function render(c: FootprintCheck): void {
        const item = document.createElement('div');
        item.className = 'net';

        const nm = document.createElement('span');
        nm.className = 'net-name';
        nm.textContent = c.ref;
        if (c.status === 'unresolved') nm.classList.add('auto');

        const detail = document.createElement('span');
        detail.className = 'net-nodes';
        const gates = c.gates.length > 1 ? ` · ${c.gates.length} gates` : '';
        detail.textContent = c.status === 'unresolved'
          ? `${c.part} → ${c.footprint ?? 'no pattern named'} · not in the libraries`
            + ` · ${c.pins} pins${gates}`
          : c.status === 'by-part-name'
            ? `${c.part} → ${c.resolved} · found by part name, the sheet names none${gates}`
            : `${c.part} → ${c.resolved}${gates}`;

        item.append(nm, detail);

        // Every row, settled or not.
        //
        // This used to be offered only where the lookup had failed, which read
        // as "there is nothing to decide here" and was wrong: a pattern that
        // resolves can still be the wrong pattern, and getting one wrong is
        // far commoner than naming one that does not exist. The report was the
        // one place in the program with the suggestions and the search in it,
        // and it shut itself the moment a name stuck.
        const pick = document.createElement('button');
        pick.type = 'button';
        pick.className = 'linkish';
        pick.textContent = openRef === c.ref
          ? 'Close'
          : c.status === 'named' ? 'Change…' : 'Find a pattern…';
        pick.addEventListener('click', () => {
          openRef = openRef === c.ref ? null : c.ref;
          void refresh();
        });
        item.append(pick);

        list.append(item);
        if (openRef === c.ref) list.append(searchPane(c));
      }


      /** The search pane for one package, wired to write the sheet. */
      function searchPane(c: FootprintCheck): HTMLElement {
        return footprintPane(
          { part: c.part, pins: c.pins, current: c.footprint ?? c.part },
          opts.suggest, opts.searchFootprints,
          (name) => {
            const n = setFootprint(opts.sheets, c.ref, name);
            if (n > 0) opts.onChange(`Footprint ${c.ref} → ${name}`);
            openRef = null;
            void refresh();
          },
          'Choosing one names the pattern on the symbol and changes nothing else.');
      }

      void refresh();
    },
  });
}

// ---------------------------------------------------------------------------
// One part on its own
// ---------------------------------------------------------------------------

/** What the pane needs to know about the thing being fitted. */
export interface FootprintNeedLite {
  /** Part name, e.g. `7404`. */
  part: string;
  /** How many pins the package brings — the strongest clue there is. */
  pins: number;
  /** What it is set to now, seeded into the box. */
  current: string;
}

/**
 * The suggestions and the search, as one block of markup.
 *
 * Shared rather than copied because the two callers have to offer the same
 * candidates in the same order: a part fitted from the Footprint Check and the
 * same part fitted from its own edit dialog cannot sensibly disagree about what
 * it probably is.
 */
function footprintPane(
  need: FootprintNeedLite,
  suggest: (n: { part: string; pins: number }) => FootprintSuggestion[],
  search: (query: string) => Promise<FootprintPartHit[]>,
  take: (name: string) => void,
  hint: string,
): HTMLElement {
  const pane = document.createElement('div');
  pane.className = 'fp-search';

  const box = document.createElement('input');
  box.type = 'search';
  box.placeholder = 'Pattern name, e.g. DIP14, SOIC8, 0805';
  // The best first guess is what it already says, then the part.
  box.value = need.current || need.part;

  const hits = document.createElement('div');
  hits.className = 'net-list';

  const note = document.createElement('p');
  note.className = 'modal-hint';

  // --- What it probably is, before the box.
  //
  // The pin count and the part number between them settle most of these, and a
  // list of two or three with a reason against each is a decision somebody can
  // actually make. The search stays underneath for the rest.
  const suggested = suggest({ part: need.part, pins: need.pins });
  if (suggested.length > 0) {
    const head = document.createElement('p');
    head.className = 'modal-hint';
    // The pin count leads when there is one. There is not always: a gate still
    // carrying a `?` belongs to no package yet, and "0 pins" is a worse thing
    // to say than nothing at all.
    head.textContent = [
      need.pins > 0 ? `${need.pins} pins` : '',
      need.part ? `a part called ${need.part}` : '',
    ].filter(Boolean).join(' and ') + ' — probably one of these:';
    pane.append(head);

    const picks = document.createElement('div');
    picks.className = 'net-list';
    for (const sg of suggested.slice(0, 4)) {
      picks.append(hitRow(sg.name, [sg.description, sg.why], true,
        same(sg.name, need.current), take));
    }
    pane.append(picks);

    const or = document.createElement('p');
    or.className = 'modal-hint';
    or.textContent = 'Or find another:';
    pane.append(or);
  }

  pane.append(box, note, hits);

  let seq = 0;
  const run = async (): Promise<void> => {
    const q = box.value.trim();
    hits.replaceChildren();
    if (q.length < 2) { note.textContent = 'Type at least two characters.'; return; }
    const mine = ++seq;
    note.textContent = 'Searching…';
    let found: FootprintPartHit[];
    try {
      found = await search(q);
    } catch (e) {
      if (mine !== seq) return;
      note.textContent = `Search failed: ${(e as Error).message}`;
      return;
    }
    if (mine !== seq) return; // a later keystroke already answered
    if (found.length === 0) {
      note.textContent = `Nothing in the PCB libraries matches “${q}”.`;
      return;
    }
    note.textContent = found.length > MAX_HITS
      ? `${found.length} matches — showing the first ${MAX_HITS}. Narrow the word.`
      : `${found.length} match${found.length === 1 ? '' : 'es'}. ` + hint;

    for (const hit of found.slice(0, MAX_HITS)) {
      hits.append(hitRow(hit.name, [hit.description, hit.library], false,
        same(hit.name, need.current), take));
    }
  };

  let timer = 0;
  box.addEventListener('input', () => {
    window.clearTimeout(timer);
    timer = window.setTimeout(() => void run(), 200);
  });
  box.addEventListener('keydown', (ev) => {
    if (ev.key === 'Enter') { ev.preventDefault(); void run(); }
  });
  queueMicrotask(() => box.focus());
  void run();
  return pane;
}

const same = (a: string, b: string): boolean =>
  a.trim().toUpperCase() === b.trim().toUpperCase();

/** One offered pattern. Marked when it is the one already fitted. */
function hitRow(
  name: string, why: (string | undefined)[], suggested: boolean, current: boolean,
  take: (name: string) => void,
): HTMLElement {
  const row = document.createElement('button');
  row.type = 'button';
  row.className = `net fp-hit${suggested ? ' fp-suggested' : ''}`;
  const nm = document.createElement('span');
  nm.className = 'net-name';
  nm.textContent = name;
  const detail = document.createElement('span');
  detail.className = 'net-nodes';
  // Saying which one it is on now matters more here than in the report, where
  // the row above the pane already says it. Without it the list is a wall of
  // near-identical package names with no mark against the one in use.
  detail.textContent = [...why, current ? 'fitted now' : ''].filter(Boolean).join(' · ');
  row.append(nm, detail);
  row.addEventListener('click', () => take(name));
  return row;
}

export interface FootprintPickerOptions extends FootprintNeedLite {
  /** Package designator, for the title — `Footprint for U1`. */
  ref: string;
  suggest(need: { part: string; pins: number }): FootprintSuggestion[];
  searchFootprints(query: string): Promise<FootprintPartHit[]>;
  /** Said under the list, because what happens next depends on the caller. */
  hint: string;
}

/**
 * Ask for a pattern for one part, away from the whole-design report.
 *
 * Resolves to the name chosen, or null when the dialog closed without one.
 * This is the way in from a part you are already pointing at, which is how
 * anybody actually arrives at "that one has the wrong footprint" — the report
 * is for finding out which parts have none.
 */
export function showFootprintPicker(opts: FootprintPickerOptions): Promise<string | null> {
  return new Promise((resolve) => {
    let picked: string | null = null;
    openDialog({
      title: `Footprint for ${opts.ref || opts.part}`,
      wide: true,
      onClose() { resolve(picked); },
      build(body, handle) {
        body.append(footprintPane(
          { part: opts.part, pins: opts.pins, current: opts.current },
          opts.suggest, opts.searchFootprints,
          (name) => { picked = name; handle.close(); },
          opts.hint));
        const bar = buttons(body);
        button(bar, 'Cancel', () => handle.close());
      },
    });
  });
}
